import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { HomeIcon, FolderIcon, UsersIcon, BarChart3Icon, SettingsIcon, LogOutIcon, PlusIcon, BuildingIcon, LayoutDashboardIcon, ChevronDownIcon, ChevronRightIcon, ChevronLeftIcon, SchoolIcon, BoxIcon, GraduationCapIcon } from 'lucide-react';
const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true); // Expanded by default for admins
  const [isOrgExpanded, setIsOrgExpanded] = useState(true);
  const userRole = 'admin';
  const getActiveItem = () => {
    if (location.pathname === '/') return 'home';
    if (location.pathname === '/workspace') return 'workspace';
    if (location.pathname === '/classes') return 'groups';
    if (location.pathname === '/reports') return 'reports';
    if (location.pathname === '/organization/overview') return 'org-overview';
    if (location.pathname.startsWith('/organization/educator/')) return 'org-overview'; // Keep Overview active for educator detail pages
    if (location.pathname === '/organization/members') return 'org-members';
    if (location.pathname === '/organization/students') return 'org-students';
    if (location.pathname === '/organization/classrooms' || location.pathname.startsWith('/organization/classrooms/')) return 'org-classrooms';
    if (location.pathname === '/organization/settings') return 'org-settings';
    return 'home';
  };
  const activeItem = getActiveItem();
  const navItems = [{
    id: 'home',
    label: 'Home',
    icon: HomeIcon,
    path: '/'
  }];
  const orgSubItems = [{
    id: 'org-overview',
    label: 'Overview',
    icon: LayoutDashboardIcon,
    tooltip: 'Organization engagement summary',
    roles: ['admin', 'educator'],
    path: '/organization/overview'
  }, {
    id: 'org-members',
    label: 'Members',
    icon: UsersIcon,
    tooltip: 'Manage users and approvals',
    roles: ['admin'],
    path: '/organization/members'
  }, {
    id: 'org-students',
    label: 'Students',
    icon: GraduationCapIcon,
    tooltip: 'Manage student records and enrollments',
    roles: ['admin'],
    path: '/organization/students'
  }, {
    id: 'org-classrooms',
    label: 'Classrooms',
    icon: SchoolIcon,
    tooltip: 'Manage classrooms and rosters',
    roles: ['admin'],
    path: '/organization/classrooms'
  }, {
    id: 'org-settings',
    label: 'Settings',
    icon: SettingsIcon,
    tooltip: 'Organization settings',
    roles: ['admin'],
    path: '/organization/settings'
  }];
  const secondaryNavItems = [{
    id: 'workspace',
    label: 'Workspace',
    icon: FolderIcon,
    path: '/workspace'
  }, {
    id: 'groups',
    label: 'Groups',
    icon: BoxIcon,
    path: '/classes'
  }, {
    id: 'reports',
    label: 'Reports',
    icon: BarChart3Icon,
    path: '/reports'
  }];
  const bottomItems = [{
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon
  }, {
    id: 'logout',
    label: 'Logout',
    icon: LogOutIcon
  }];
  const visibleOrgSubItems = orgSubItems.filter(item => item.roles.includes(userRole));
  const showOrganization = userRole !== 'independent';
  const handleNavigation = (path: string) => {
    navigate(path);
  };
  const Tooltip = ({
    children,
    label,
    show
  }: {
    children: React.ReactNode;
    label: string;
    show: boolean;
  }) => <div className="relative" data-id="element-2243">
      {children}
      <AnimatePresence data-id="element-2244">
        {show && !isExpanded && <motion.div initial={{
        opacity: 0,
        x: -5
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -5
      }} transition={{
        duration: 0.15
      }} className="absolute left-full ml-3 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-black/90 text-white text-xs font-medium rounded-lg whitespace-nowrap pointer-events-none shadow-lg z-[9999]" data-id="element-2245">
            {label}
          </motion.div>}
      </AnimatePresence>
    </div>;
  return <motion.aside initial={false} animate={{
    width: isExpanded ? 240 : 64
  }} transition={{
    duration: 0.3,
    ease: 'easeInOut'
  }} className="h-full flex flex-col bg-white shadow-sm z-40 relative" data-id="element-2246">
      {/* Toggle Button */}
      <button onClick={() => setIsExpanded(!isExpanded)} className="absolute -right-3 top-6 w-6 h-6 bg-white border-2 border-custom rounded-full flex items-center justify-center shadow-md hover:bg-surface transition-colors z-50" aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'} data-id="element-2247">
        {isExpanded ? <ChevronLeftIcon size={14} className="text-text-secondary" data-id="element-2248" /> : <ChevronRightIcon size={14} className="text-text-secondary" data-id="element-2249" />}
      </button>

      <div className="p-3 pt-6" data-id="element-2250">
        <Tooltip label="Create Assessment" show={hoveredItem === 'create'} data-id="element-2251">
          <motion.button onMouseEnter={() => setHoveredItem('create')} onMouseLeave={() => setHoveredItem(null)} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className={`${isExpanded ? 'w-full' : 'w-10'} h-10 rounded-full bg-black text-white flex items-center ${isExpanded ? 'justify-center gap-2 px-4' : 'justify-center'} shadow-md hover:shadow-lg transition-all duration-200`} aria-label="Create new assessment" data-id="element-2252">
            <PlusIcon size={20} data-id="element-2253" />
            {isExpanded && <span className="text-sm font-semibold" data-id="element-2254">Create</span>}
          </motion.button>
        </Tooltip>
      </div>

      <nav className="flex-1 px-3 flex flex-col gap-3 mt-4 overflow-y-auto" data-id="element-2255">
        {/* Organization Section */}
        {showOrganization && <div className="flex flex-col gap-2" data-id="element-2256">
            {/* Organization Header */}
            <Tooltip label="Organization" show={hoveredItem === 'organization'} data-id="element-2257">
              <motion.button onClick={() => setIsOrgExpanded(!isOrgExpanded)} onMouseEnter={() => setHoveredItem('organization')} onMouseLeave={() => setHoveredItem(null)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className={`${isExpanded ? 'w-full justify-between px-4' : 'w-10 justify-center'} h-10 rounded-xl flex items-center transition-all duration-200 ${activeItem.startsWith('org-') ? 'bg-[#00B75F]/10 text-[#00B75F] border-l-4 border-[#00B75F]' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-id="element-2258">
                <div className="flex items-center gap-3" data-id="element-2259">
                  <BuildingIcon size={20} className="flex-shrink-0" data-id="element-2260" />
                  {isExpanded && <span className="text-sm font-medium" data-id="element-2261">Organization</span>}
                </div>
                {isExpanded && <div className="flex-shrink-0" data-id="element-2262">
                    {isOrgExpanded ? <ChevronDownIcon size={16} data-id="element-2263" /> : <ChevronRightIcon size={16} data-id="element-2264" />}
                  </div>}
              </motion.button>
            </Tooltip>

            {/* Organization Sub-items */}
            <AnimatePresence data-id="element-2265">
              {isOrgExpanded && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className={`flex flex-col gap-2 overflow-hidden ${isExpanded ? 'pl-4' : ''}`} data-id="element-2266">
                  {visibleOrgSubItems.map(item => {
              const Icon = item.icon;
              const isActive = activeItem === item.id;
              return <Tooltip key={item.id} label={item.label} show={hoveredItem === item.id} data-id="element-2267">
                        <motion.button onClick={() => handleNavigation(item.path)} onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} className={`${isExpanded ? 'w-full justify-start px-4' : 'w-10 justify-center'} h-10 rounded-xl flex items-center gap-3 transition-all duration-200 ${isActive ? 'bg-[#00B75F] text-white shadow-md' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-id="element-2268">
                          <Icon size={18} className="flex-shrink-0" data-id="element-2269" />
                          {isExpanded && <span className="text-sm font-medium" data-id="element-2270">
                              {item.label}
                            </span>}
                        </motion.button>
                      </Tooltip>;
            })}
                </motion.div>}
            </AnimatePresence>
          </div>}

        {/* Home */}
        {navItems.map(item => {
        const Icon = item.icon;
        const isActive = activeItem === item.id;
        return <Tooltip key={item.id} label={item.label} show={hoveredItem === item.id} data-id="element-2271">
              <motion.button onClick={() => handleNavigation(item.path)} onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className={`${isExpanded ? 'w-full justify-start px-4' : 'w-10 justify-center'} h-10 rounded-xl flex items-center gap-3 transition-all duration-200 ${isActive ? 'bg-[#00B75F] text-white shadow-md' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-id="element-2272">
                <Icon size={20} className="flex-shrink-0" data-id="element-2273" />
                {isExpanded && <span className="text-sm font-medium" data-id="element-2274">{item.label}</span>}
              </motion.button>
            </Tooltip>;
      })}

        <div className="h-px bg-gray-200 my-2" data-id="element-2275" />

        {/* Secondary Navigation */}
        {secondaryNavItems.map(item => {
        const Icon = item.icon;
        const isActive = activeItem === item.id;
        return <Tooltip key={item.id} label={item.label} show={hoveredItem === item.id} data-id="element-2276">
              <motion.button onClick={() => handleNavigation(item.path)} onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className={`${isExpanded ? 'w-full justify-start px-4' : 'w-10 justify-center'} h-10 rounded-xl flex items-center gap-3 transition-all duration-200 ${isActive ? 'bg-[#00B75F] text-white shadow-md' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-id="element-2277">
                <Icon size={20} className="flex-shrink-0" data-id="element-2278" />
                {isExpanded && <span className="text-sm font-medium" data-id="element-2279">{item.label}</span>}
              </motion.button>
            </Tooltip>;
      })}
      </nav>

      <div className="px-3 pb-6 pt-4 border-t border-gray-100 flex flex-col gap-3" data-id="element-2280">
        {bottomItems.map(item => {
        const Icon = item.icon;
        return <Tooltip key={item.id} label={item.label} show={hoveredItem === item.id} data-id="element-2281">
              <motion.button onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className={`${isExpanded ? 'w-full justify-start px-4' : 'w-10 justify-center'} h-10 rounded-xl bg-gray-50 text-gray-600 hover:bg-gray-100 flex items-center gap-3 transition-all duration-200`} data-id="element-2282">
                <Icon size={20} className="flex-shrink-0" data-id="element-2283" />
                {isExpanded && <span className="text-sm font-medium" data-id="element-2284">{item.label}</span>}
              </motion.button>
            </Tooltip>;
      })}
      </div>
    </motion.aside>;
};
export default Sidebar;